import { apiSlice } from './apiSlice';
import {
  AddBankAccountRequest,
  AddBankAccountResponse,
  AddUPIAccountRequest,
  AddUPIAccountResponse,
  BankAccountsResponse,
  CreateDepositWithdrawResponse,
  CreateUpiTransactionResponse,
  DeleteAccountResponse,
  DepositAccountResponse,
  DepositWithdrawQueryParams,
  DepositWithdrawRequest,
  DepositWithdrawResponse,
  UpiAccountResponse,
  UploadFile,
} from '@/types/Account';

export const accountApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    addBankAccount: builder.mutation<AddBankAccountResponse, AddBankAccountRequest>({
      query: (body) => ({
        url: '/bank',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Account'],
    }),
    addUpiAccount: builder.mutation<AddUPIAccountResponse, AddUPIAccountRequest>({
      query: (body) => ({
        url: '/upi',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Account'],
    }),
    depositWithdrawRequest: builder.mutation<
      CreateDepositWithdrawResponse | CreateUpiTransactionResponse,
      DepositWithdrawRequest
    >({
      query: ({ method, ...body }) => ({
        url: `/${method}/deposit-withdraw-request`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Account', 'User'],
    }),
    deleteAccount: builder.mutation<DeleteAccountResponse, { id: string; type: 'upi' | 'bank' }>({
      query: ({ id, type }) => ({
        url: `/${type}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Account'],
    }),
    getBankAccount: builder.query<BankAccountsResponse, void>({
      query: () => ({
        url: '/bank',
        method: 'GET',
      }),
      providesTags: ['Account'],
    }),
    // limit optional hai: backend default 10 par rukta hai, aur jiske paas
    // usse zyada saved accounts hain uske baaki account withdraw ki list se
    // gayab ho jaate the.
    getUpiAccount: builder.query<UpiAccountResponse, { page?: number; limit?: number } | void>({
      query: (params) => ({
        url: '/upi',
        method: 'GET',
        params: params || undefined,
      }),
      providesTags: ['Account'],
    }),
    getDepositWithdraw: builder.query<DepositWithdrawResponse, DepositWithdrawQueryParams>({
      query: (params) => ({
        url: '/banker/my-deposit-withdraw',
        method: 'GET',
        params,
      }),
      providesTags: ['Account'],
    }),
    uploadFile: builder.mutation<UploadFile, File>({
      query: (file) => {
        const formData = new FormData();
        formData.append('file', file);

        return {
          url: '/upload',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Account'],
    }),
    getDepositAccounts: builder.query<DepositAccountResponse, void>({
      query: () => ({
        url: '/banker/show-account',
        method: 'GET',
      }),
      providesTags: ['Account'],
    }),

    getPaymentMethodConfig: builder.query<{
      depositUpi: boolean; depositBank: boolean; depositWhatsapp: boolean; depositCrypto: boolean;
      withdrawUpi: boolean; withdrawBank: boolean; withdrawWhatsapp: boolean; withdrawCrypto: boolean;
      whatsappDepositNumber: string; whatsappWithdrawNumber: string;
    }, void>({
      query: () => ({ url: '/system/payment-methods', method: 'GET' }),
    }),

    getMyTurnovers: builder.query<{
      data: Array<{
        depositId: string;
        required: number;
        completed: number;
        remaining: number;
        progress: number;
        status: 'Pending' | 'Completed';
        warning?: string;
      }>;
      pagination: { currentPage: number; totalPage: number; totalItems: number; limit: number };
    }, { status?: 'Pending' | 'Completed'; page?: number; limit?: number }>({
      query: (params) => ({ url: '/my-wallet', method: 'GET', params }),
      providesTags: ['Account'],
    }),

    getWalletSummary: builder.query<{
      totalBalance: number;
      depositBalance: number;
      bonusBalance: number;
      lockedBalance: number;
      turnoverLocked: number;
      availableWithdrawals: number;
    }, void>({
      query: () => ({ url: '/my-wallet/summary', method: 'GET' }),
      providesTags: ['Account'],
    }),
  }),
});

export const {
  useAddBankAccountMutation,
  useAddUpiAccountMutation,
  useGetBankAccountQuery,
  useDeleteAccountMutation,
  useDepositWithdrawRequestMutation,
  useGetDepositWithdrawQuery,
  useUploadFileMutation,
  useGetUpiAccountQuery,
  useGetDepositAccountsQuery,
  useGetPaymentMethodConfigQuery,
  useGetMyTurnoversQuery,
  useGetWalletSummaryQuery,
} = accountApi;
